import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Patch, Partialize } from "@freightsimple/shared/types";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { DimensionsInput } from "@freightsimple/ui-core/Controls/DimensionsInput";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { T } from "@freightsimple/ui-core/Typography/T";

import { LineItemsConvertUnitsButton } from "@/Shared/LineItems/Buttons/LineItemsConvertUnitsButton";
import { Units } from "@freightsimple/ui-core/Data/Units";
import DangerousGoodsInput from "@/Shared/DangerousGoods/Inputs/DangerousGoodsInput";
import LineItemsHandlingUnitTypeInput from "@/Shared/LineItems/Inputs/LineItemsHandlingUnitTypeInput";
import LineItemsIsStackableInput from "@/Shared/LineItems/Inputs/LineItemsIsStackableInput";
import LineItemsNmfcInput from "@/Shared/LineItems/Inputs/LineItemsNmfcInput";
import LineItemsTemperatureHandlingInput from "@/Shared/LineItems/Inputs/LineItemsTemperatureHandlingInput";
import { useLineItemsLookupNmfcCode } from "@/Shared/LineItems/Hooks/useLineItemsLookupNmfcCode";
import { useNmfcDescription } from "@/Slices/nmfcDescriptionCacheSlice";
import type { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { SavedCommoditiesSkuIdentifierQuestionBubble } from "../../Bubbles/SavedCommoditiesSkuIdentifierQuestionBubble";
import { savedCommoditiesErrorMessages } from "../../Validators/savedCommoditiesErrorMessages";
import { useLineItemsShipmentLimits } from "@/Shared/LineItems/Validators/useLineItemsShipmentLimits";

interface AddModifySavedCommodityDialogBodyProps {
  onConfirm: (savedCommodity: SavedCommodity) => Promise<void>;
  onCancel: () => void;
  savedCommodityToModify: SavedCommodity | undefined;
}

type AddSavedCommodityType = Partialize<
  SavedCommodity,
  | "handlingUnitType"
  | "isStackable"
  | "nmfcItemNumber"
  | "skuIdentifier"
  | "lengthInches"
  | "widthInches"
  | "heightInches"
>;

function emptySavedCommodity(): AddSavedCommodityType {
  return {
    savedCommodityId: uuidv4(),
    description: "",
    handlingUnitType: undefined,
    isDangerous: false,
    temperatureHandling: TemperatureHandling.NoSpecialHandling,
  };
}

export function AddModifySavedCommodityDialogBody(
  props: AddModifySavedCommodityDialogBodyProps,
) {
  const [savedCommodity, setSavedCommodity] = useState<AddSavedCommodityType>(
    props.savedCommodityToModify || emptySavedCommodity(),
  );
  const [forceValidation, setForceValidation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [skuError, setSkuError] = useState<string | undefined>();
  const [saveFailed, setSaveFailed] = useState(false);
  const [units, setUnits] = useState<Units>(Units.Imperial);
  const { onCancel } = props;
  const {
    nmfcLookupInProgress,
    lookupNmfcCodeNoDimensions,
    nmfcErrorMessage,
    clearNmfcError,
    nmfcSubItems,
  } = useLineItemsLookupNmfcCode();

  const nmfcDescription = useNmfcDescription(savedCommodity.nmfcItemNumber);

  const shipmentLimits = useLineItemsShipmentLimits();
  const errorMessages = savedCommoditiesErrorMessages(
    savedCommodity,
    shipmentLimits,
  );

  function isValid() {
    const anyErrorMessages = [
      errorMessages.handlingUnitType,
      errorMessages.description,
      errorMessages.skuIdentifier,
      errorMessages.isStackable,
      errorMessages.dimensionsLength,
      errorMessages.dimensionsWidth,
      errorMessages.dimensionsHeight,
      errorMessages.dangerousGoods,
      nmfcErrorMessage,
    ].some((e) => e !== undefined);

    return !anyErrorMessages;
  }

  async function onConfirm() {
    setForceValidation(true);

    if (!isValid()) {
      return;
    }

    try {
      setSaving(true);
      setSaveFailed(false);
      await props.onConfirm(savedCommodity as SavedCommodity);
    } catch (e) {
      if (e instanceof ResponseError && e.response.status === 409) {
        setSkuError("This SKU is already in use by another saved commodity");
      } else {
        console.error("Error saving commodity", { e });
        setSaveFailed(true);
      }
    } finally {
      setSaving(false);
    }
  }

  function onHandlingUnitTypeChange(value: HandlingUnitType) {
    setSavedCommodity((sc) => ({
      ...sc,
      handlingUnitType: value,
    }));
  }

  function onDescriptionChange(value: string) {
    setSavedCommodity((sc) => ({ ...sc, description: value }));
  }

  async function onNmfcChange(newNmfcCode: string | undefined) {
    if (newNmfcCode) {
      const response = await lookupNmfcCodeNoDimensions(newNmfcCode);

      // Only save the NMFC code if validation was successful
      if (response) {
        setSavedCommodity((sc) => ({
          ...sc,
          nmfcItemNumber: newNmfcCode,
        }));
      }
      // If validation fails, don't save the NMFC code (keeps it undefined)
    } else {
      setSavedCommodity((sc) => ({ ...sc, nmfcItemNumber: undefined }));
    }
  }

  function onIsStackableChange(value: boolean) {
    setSavedCommodity((sc) => ({ ...sc, isStackable: value }));
  }

  function onTemperatureHandlingChange(value: TemperatureHandling) {
    setSavedCommodity((sc) => ({ ...sc, temperatureHandling: value }));
  }

  function onDangerousChange(value: Patch<LineItem>) {
    setSavedCommodity((sc) => ({ ...sc, ...value }) as AddSavedCommodityType);
  }

  function onSkuIdentifierChange(value: string) {
    setSkuError(undefined);
    setSavedCommodity((sc) => ({
      ...sc,
      skuIdentifier: value || undefined,
    }));
  }

  function onLengthInchesChange(value: number | undefined) {
    setSavedCommodity((sc) => ({ ...sc, lengthInches: value }));
  }

  function onWidthInchesChange(value: number | undefined) {
    setSavedCommodity((sc) => ({ ...sc, widthInches: value }));
  }

  function onHeightInchesChange(value: number | undefined) {
    setSavedCommodity((sc) => ({ ...sc, heightInches: value }));
  }

  return (
    <ControlPresentationProvider presentation="underlined">
      <div id="AddModifySavedCommodityDialogBody">
        {/* No `align="start"` anywhere down this chain: it shrink-wraps every
            child, so the two columns hugged their content, the footer's
            justify="between" had no width to spread across, and each control's
            own `width="full"` resolved against a box already only as wide as
            its text. */}
        <Stack gap={8}>
          <Grid columns={2} gap="lg">
            <Stack gap={4}>
              <T weight="bold">Contents</T>
              <LineItemsHandlingUnitTypeInput
                value={savedCommodity.handlingUnitType}
                onChange={onHandlingUnitTypeChange}
                usePluralForm={true}
                placeholder="Handling Unit Type"
                errorMessage={errorMessages.handlingUnitType}
                forceValidation={forceValidation}
              />
              <ShowFormErrors show={forceValidation}>
                <FormItem label="Description" error={errorMessages.description}>
                  <TextInput
                    type="text"
                    name="contents_description"
                    value={savedCommodity.description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    width="full"
                    placeholder="Enter Description"
                  />
                </FormItem>
              </ShowFormErrors>
              <LineItemsNmfcInput
                onChange={onNmfcChange}
                nmfcCode={savedCommodity.nmfcItemNumber}
                loading={nmfcLookupInProgress}
                description={nmfcDescription}
                confirmed={savedCommodity.nmfcItemNumber !== undefined}
                errorMessage={nmfcErrorMessage}
                clearError={clearNmfcError}
                subItems={nmfcSubItems}
              />
              <ShowFormErrors show={forceValidation || skuError !== undefined}>
                <FormItem
                  label={
                    <>
                      SKU Identifier (optional){" "}
                      <SavedCommoditiesSkuIdentifierQuestionBubble />
                    </>
                  }
                  error={skuError ?? errorMessages.skuIdentifier}
                >
                  <TextInput
                    type="text"
                    name="sku_identifier"
                    value={savedCommodity.skuIdentifier ?? ""}
                    onChange={(e) => onSkuIdentifierChange(e.target.value)}
                    width="full"
                    placeholder="Enter SKU"
                  />
                </FormItem>
              </ShowFormErrors>
              <Stack gap={0} align="start">
                <T size="xs" color="muted">
                  Dimensions (optional)
                </T>
                <DimensionsInput
                  length={savedCommodity.lengthInches}
                  width={savedCommodity.widthInches}
                  height={savedCommodity.heightInches}
                  onLengthChange={onLengthInchesChange}
                  onWidthChange={onWidthInchesChange}
                  onHeightChange={onHeightInchesChange}
                  lengthErrorMessage={errorMessages.dimensionsLength}
                  widthErrorMessage={errorMessages.dimensionsWidth}
                  heightErrorMessage={errorMessages.dimensionsHeight}
                  forceValidation={forceValidation}
                  units={units}
                  maxSizeWarning={100}
                  minSizeWarning={2}
                  testIdPrefix="commodity"
                />
              </Stack>
            </Stack>

            <Stack gap={4}>
              <T weight="bold">Special Handling</T>
              <LineItemsIsStackableInput
                isStackable={savedCommodity.isStackable}
                onChange={onIsStackableChange}
                usePlural={true}
                handlingUnitType={savedCommodity.handlingUnitType}
                errorMessage={errorMessages.isStackable}
                forceValidation={forceValidation}
              />
              <LineItemsTemperatureHandlingInput
                temperatureHandling={savedCommodity.temperatureHandling}
                onChange={onTemperatureHandlingChange}
                errorMessage={errorMessages.temperatureHandling}
                forceValidation={forceValidation}
              />
              <DangerousGoodsInput
                lineItem={savedCommodity}
                onChange={function (update) {
                  onDangerousChange(update);
                }}
                errorMessage={errorMessages.dangerousGoods}
                forceValidation={forceValidation}
                // Saved commodities are reusable templates with no shipment or
                // country context, and net explosive quantity depends on the
                // amount shipped, so it's captured per shipment on the line
                // item, not here.
                canadianTdgRules={false}
              />
            </Stack>
          </Grid>

          {saveFailed && (
            <Banner variant="danger">
              We couldn&apos;t save this commodity. Please try again.
            </Banner>
          )}

          <Row justify="between">
            <LineItemsConvertUnitsButton
              units={units}
              setUnits={setUnits}
              dimensionsOnly
            />
            <Row gap={4}>
              <Button
                onClick={onCancel}
                disabled={nmfcLookupInProgress || saving}
                disabledMessage="Wait for the current operation to finish"
              >
                Cancel
              </Button>
              <Button
                primary
                onClick={onConfirm}
                disabled={nmfcLookupInProgress || saving}
                disabledMessage="Wait for the current operation to finish"
                data-testid="save-commodity-button"
              >
                {nmfcLookupInProgress
                  ? "Validating NMFC..."
                  : saving
                    ? "Saving..."
                    : "Save Commodity"}
              </Button>
            </Row>
          </Row>
        </Stack>
      </div>
    </ControlPresentationProvider>
  );
}
